/**
 * Authentication Service
 * Handles all authentication-related API calls
 */

import api from './api';

const authService = {
  /**
   * Login user
   * @param {Object} credentials - User credentials
   * @param {string} credentials.email - User email
   * @param {string} credentials.password - User password
   * @returns {Promise} Login response with user and tokens
   */
  login: async (credentials) => {
    try {
      const response = await api.post('/auth/login', {
        email: credentials.email,
        password: credentials.password,
      });

      // Note: axios interceptor already unwraps response.data
      // Backend returns: { success: true, data: { user, tokens }, meta }
      if (response?.success && response?.data) {
        const { user, tokens } = response.data;
        
        return {
          user: {
            id: user.id,
            email: user.email,
            name: `${user.firstName} ${user.lastName}`,
            firstName: user.firstName,
            lastName: user.lastName,
            role: user.role,
            permissions: user.permissions,
            avatar: user.avatar,
          },
          token: tokens.accessToken,
          refreshToken: tokens.refreshToken,
          expiresIn: tokens.expiresIn,
        };
      }
      
      throw new Error('Invalid response format from server');
    } catch (error) {
      
      // Handle API errors - backend returns { error: true, message: "..." }
      if (error.response?.data) {
        const errorData = error.response.data;
        const message = errorData.message || errorData.error || 'An error occurred during login';
        throw new Error(message);
      }
      
      // Handle network errors
      if (error.message) {
        throw error;
      }
      
      throw new Error('Unable to connect to the server. Please try again.');
    }
  },

  /**
   * Logout user
   * @returns {Promise} Logout response
   */
  logout: async () => {
    try {
      // Clear tokens - no backend call needed for mock logout
      // In production, you might want to invalidate the token on the server
      return Promise.resolve({ success: true });
    } catch (error) {
      throw error;
    }
  },

  /**
   * Refresh authentication token
   * @param {string} refreshToken - Refresh token
   * @returns {Promise} New tokens
   */
  refreshToken: async (refreshToken) => {
    try {
      const response = await api.post('/auth/refresh', { refreshToken });
      
      if (response.data && response.data.success) {
        return {
          token: response.data.data.accessToken,
          refreshToken: response.data.data.refreshToken,
          expiresIn: response.data.data.expiresIn,
        };
      }
      
      throw new Error('Invalid response format from server');
    } catch (error) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw error;
    }
  },

  /**
   * Verify current token
   * @returns {Promise} Verification response
   */
  verifyToken: async () => {
    try {
      return await api.get('/auth/verify');
    } catch (error) {
      throw error;
    }
  },

  /**
   * Request password reset
   * @param {string} email - User email
   * @returns {Promise} Reset request response
   */
  forgotPassword: async (email) => {
    try {
      return await api.post('/auth/forgot-password', { email });
    } catch (error) {
      throw error;
    }
  },

  /**
   * Reset password with token
   * @param {Object} data - Reset data
   * @param {string} data.token - Reset token
   * @param {string} data.password - New password
   * @returns {Promise} Reset response
   */
  resetPassword: async (data) => {
    try {
      return await api.post('/auth/reset-password', data);
    } catch (error) {
      throw error;
    }
  },

  /**
   * Change password (authenticated user)
   * @param {Object} data - Password change data
   * @param {string} data.currentPassword - Current password
   * @param {string} data.newPassword - New password
   * @returns {Promise} Change response
   */
  changePassword: async (data) => {
    try {
      return await api.post('/auth/change-password', data);
    } catch (error) {
      throw error;
    }
  },
};

export default authService;
